import { computed } from 'vue'
import { storeToRefs } from 'pinia'
import { useJournalEntriesStore } from '@/views/apps/journals/store/journalEntriesStore'
import { useJournalDetailStore } from '@/views/apps/journals/store/journalDetailStore'
import { useJournalFilterStore } from '@/views/apps/journals/store/journalFilterStore'
import { resolveChoiceLabel } from '@/views/apps/journals/utils/filterTree'

interface FilterChip {
  attrKey: string
  label: string
}

interface UseJournalDetailFiltersParams {
  journalId: { value: number }
}

function isRangeValue(v: unknown): v is { from?: string; to?: string } {
  return typeof v === 'object' && v !== null && ('from' in v || 'to' in v)
}

export function useJournalDetailFilters({ journalId }: UseJournalDetailFiltersParams) {
  const entriesStore = useJournalEntriesStore()
  const detailStore = useJournalDetailStore()
  const journalFilterStore = useJournalFilterStore()

  const { journalAttributes } = storeToRefs(detailStore)

  function isFilterActive(v: unknown): boolean {
    if (v == null) {
      return false
    }

    if (Array.isArray(v)) {
      return v.length > 0
    }

    if (isRangeValue(v)) {
      return !!(v.from || v.to)
    }

    return v !== ''
  }

  const hasActiveFilters = computed(() =>
    Object.values(entriesStore.entryFilters).some(v => isFilterActive(v)),
  )

  function getChoiceLabel(attrKey: string, id: string | number): string {
    return resolveChoiceLabel(
      journalFilterStore.searchChoices,
      journalFilterStore.filterChoices,
      attrKey,
      id,
    )
  }

  const activeFilterChips = computed<FilterChip[]>(() => {
    const chips: FilterChip[] = []
    const attrs = journalAttributes.value

    for (const [attrKey, value] of Object.entries(entriesStore.entryFilters)) {
      if (!isFilterActive(value)) {
        continue
      }

      const attr = attrs.find(a => String(a.id) === attrKey)
      const name = attr?.short_name || attr?.attribute_name || attrKey
      const attrType = attr?.attribute_type

      const isChoiceType
        = !!attrType
        && ['choice', 'option', 'multiple_choice', 'multi_option', 'content_object'].includes(attrType)

      let valueLabel = ''

      if (typeof value === 'boolean') {
        valueLabel = value ? 'Да' : 'Нет'
      } else if (attrType === 'file') {
        valueLabel = value === true ? 'Добавлен' : 'Не добавлен'
      } else if (Array.isArray(value) && isChoiceType) {
        valueLabel = value.map(id => getChoiceLabel(attrKey, id)).join(', ')
      } else if (Array.isArray(value)) {
        valueLabel = value.join(', ')
      } else if (isRangeValue(value)) {
        valueLabel = [value.from, value.to].filter(Boolean).join(' — ')
      } else if (isChoiceType && value) {
        valueLabel = getChoiceLabel(attrKey, value as string | number)
      } else {
        valueLabel = String(value)
      }

      chips.push({ attrKey, label: `${name}: ${valueLabel}` })
    }

    return chips
  })

  function applyEntryFiltersAndFetch() {
    entriesStore.applyEntryFiltersAndFetch(journalId.value)
  }

  function removeFilterChip(attrKey: string) {
    entriesStore.setEntryFilter(attrKey, null)
    applyEntryFiltersAndFetch()
  }

  function resetAllFilters() {
    entriesStore.clearEntryFilters()
    applyEntryFiltersAndFetch()
  }

  return {
    activeFilterChips,
    applyEntryFiltersAndFetch,
    hasActiveFilters,
    isFilterActive,
    removeFilterChip,
    resetAllFilters,
  }
}
